import React from 'react';
import { Link } from 'react-router-dom';
import './Shawn.css';
import PageHero from './PageHero';

const Shawn = () => {
  const skillsData = [
    {
      name: "HTML",
      description: "Semantic markup and proper document structure",
      icon: "/html.svg",
      rating: 95
    },
    {
      name: "CSS",
      description: "Responsive design and modern styling techniques",
      icon: "/css.svg",
      rating: 85
    },
    {
      name: "JavaScript",
      description: "Interactive features and dynamic functionality",
      icon: "/js.svg",
      rating: 75
    },
    {
      name: "PHP",
      description: "Backend development and server-side logic",
      icon: "/php.svg",
      rating: 80
    },
    {
      name: "SQL",
      description: "Database design and secure data management",
      icon: "/sql.svg",
      rating: 70
    },
    {
      name: "React.js",
      description: "Modern frontend development and component architecture",
      icon: "/react.svg",
      rating: 75
    },
    {
      name: "Java",
      description: "Minecraft plugin development and object-oriented programming",
      icon: "/java.svg",
      rating: 80
    }
  ];

  const handleDownload = (filePath, title) => {
    // For PDF files, we'll open in a new tab and let the browser handle the download
    const link = document.createElement('a');
    link.href = filePath;
    link.download = `${title}.pdf`;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    
    // Add to DOM, click, and remove
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    // Also try to trigger download directly
    fetch(filePath)
      .then(response => response.blob())
      .then(blob => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${title}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      })
      .catch(error => {
        console.log('Download failed, opening in new tab:', error);
        window.open(filePath, '_blank');
      });
  };

  return (
    <>
      <PageHero
        eyebrow="Crew / Shawn De Pedro"
        title="Shawny"
        lede="Full-stack developer and creative coder. Seven years in, from first JavaScript experiments to production applications, interactive sites, and Minecraft plugins."
        meta={['Founder', '7 years coding', 'React · Node · PostgreSQL']}
      >
        <Link to="/contact?reason=quote" className="btn btn--primary">
          Work with me <span className="btn-arrow">→</span>
        </Link>
        <Link to="/crew" className="btn btn--ghost">
          Back to crew
        </Link>
      </PageHero>

      <main className="shawn-main-content" id="main">
        {/* About Section */}
        <section className="section shawn-about-section">
          <div className="container container--narrow">
            <p className="eyebrow">About</p>
            <p className="shawn-about-text reveal reveal-up">
              I'm Shawny, a developer with 7 years of experience in web development and
              programming. My journey began with simple JavaScript and has grown into
              full-stack applications, interactive websites, and even Minecraft plugins.
              I like turning ideas into working software and picking up whatever the
              next project demands.
            </p>
          </div>
        </section>

        {/* Skills Section */}
        <section className="section section--alt shawn-skills-section">
          <div className="container">
            <div className="section-head">
              <p className="eyebrow">Toolkit</p>
              <h2 className="section-title reveal reveal-up">Technical skills.</h2>
              <p className="section-lede reveal reveal-up" style={{ '--reveal-delay': '0.08s' }}>
                Proficiency judged honestly, against real project complexity.
              </p>
            </div>

            <ul className="shawn-skills-grid" data-stagger="0.05">
              {skillsData.map((skill) => (
                <li className="shawn-skill-card reveal reveal-up" key={skill.name}>
                  <div className="shawn-skill-header">
                    <img src={skill.icon} alt="" className="shawn-skill-icon" />
                    <div className="shawn-skill-info">
                      <h3 className="shawn-skill-name">{skill.name}</h3>
                      <p className="shawn-skill-description">{skill.description}</p>
                    </div>
                    <span className="shawn-rating-text">{skill.rating}</span>
                  </div>
                  <div className="shawn-skill-rating">
                    <span className="shawn-rating-bar" style={{ width: `${skill.rating}%` }} />
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* Projects Section */}
        <section className="section shawn-projects-section">
          <div className="container">
            <div className="section-head">
              <p className="eyebrow">Selected work</p>
              <h2 className="section-title reveal reveal-up">Featured projects.</h2>
              <p className="section-lede reveal reveal-up" style={{ '--reveal-delay': '0.08s' }}>
                Work across different stacks — each one shipped, not prototyped.
              </p>
            </div>

            <div className="shawn-projects-grid" data-stagger="0.06">
              <div className="shawn-project-card reveal reveal-up">
                <div className="shawn-video-col">
                  <div className="shawn-video-container">
                    <iframe 
                      src="https://www.youtube.com/embed/08xWrLK1HtA?si=hsBhLQ9-g5LGy8lL" 
                      title="E-commerce App Demo" 
                      frameBorder="0" 
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" 
                      referrerPolicy="strict-origin-when-cross-origin" 
                      allowFullScreen>
                    </iframe>
                  </div>
                </div>
                <div className="shawn-info-col shawn-project-info">
                  <div className="shawn-project-timestamp">Created: <time dateTime="2020-06-15">June 15, 2020</time></div>
                  <h3>Full-Stack E-commerce Application</h3>
                  <p>
                    A comprehensive e-commerce platform built from scratch using modern web technologies. This project demonstrates my ability to create complete, production-ready applications with user authentication, database management, and secure payment processing.
                  </p>
                  <h4>Key Features:</h4>
                  <ul>
                    <li>User authentication and account management</li>
                    <li>Shopping cart with multi-quantity purchases</li>
                    <li>Profile customization and settings</li>
                  </ul>
                  <h4>Used Languages:</h4>
                  <div className="shawn-tech-stack">
                    <img src="/html.svg" alt="HTML" title="HTML5" data-tooltip="HTML5 - Semantic markup, forms, multimedia support" />
                    <img src="/css.svg" alt="CSS" title="CSS3" data-tooltip="CSS3 - Flexbox, Grid, animations, responsive design" />
                    <img src="/js.svg" alt="JavaScript" title="JavaScript" data-tooltip="JavaScript - ES6+, DOM manipulation, async/await" />
                    <img src="/php.svg" alt="PHP" title="PHP" data-tooltip="PHP - Server-side scripting, MySQL integration, sessions" />
                    <img src="/sql.svg" alt="SQL" title="MySQL" data-tooltip="MySQL - Database design, queries, user authentication" />
                  </div>
                  <p className="shawn-project-note">
                    <strong>Note:</strong> This project demonstrates full-stack development capabilities with PHP and MySQL, showcasing secure user authentication and comprehensive e-commerce functionality.
                  </p>
                </div>
              </div>
              <div className="shawn-project-card reveal reveal-up">
                <div className="shawn-video-col">
                  <div className="shawn-video-container">
                    <iframe 
                      src="https://www.youtube.com/embed/GkAm-tNrui8?si=p3fj_znN6w0JZ3MM" 
                      title="AI Workout App Demo" 
                      frameBorder="0" 
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" 
                      referrerPolicy="strict-origin-when-cross-origin" 
                      allowFullScreen>
                    </iframe>
                  </div>
                </div>
                <div className="shawn-info-col shawn-project-info">
                  <div className="shawn-project-timestamp">Created: <time dateTime="2024-06-29">June 2, 2023</time></div>
                  <h3>Full-Stack AI Workout Application</h3>
                  <p>
                    A comprehensive fitness application that leverages AI to help users create personalized workout routines. This full-stack solution features an intuitive user interface for managing exercises, sets, reps, and weight tracking, with the ability to export workouts as PDF files for printing or sharing.
                  </p>
                  <h4>Key Features:</h4>
                  <ul>
                    <li>User authentication and account management</li>
                    <li>Custom workout creation and management</li>
                    <li>PDF export for printing or sharing</li>
                    <li>Responsive design for all devices</li>
                  </ul>
                  <h4>Used Languages:</h4>
                  <div className="shawn-tech-stack">
                    <img src="/html.svg" alt="HTML" title="HTML5" data-tooltip="HTML5 - Semantic markup, accessibility, modern web standards" />
                    <img src="/css.svg" alt="CSS" title="CSS3" data-tooltip="CSS3 - Flexbox, Grid, animations, responsive design" />
                    <img src="/js.svg" alt="JavaScript" title="JavaScript" data-tooltip="JavaScript - ES6+, React hooks, component lifecycle" />
                    <img src="/mongodb.svg" alt="MongoDB" title="MongoDB" data-tooltip="MongoDB - NoSQL database, user data storage, workout tracking" />
                    <img src="/react.svg" alt="React" title="React" data-tooltip="React - Component architecture, state management, PDF generation" />
                    <img src="/nodejs.svg" alt="Node.js" title="Node.js" data-tooltip="Node.js - Backend API, Express.js, user authentication" />
                  </div>
                  <p className="shawn-project-note">
                    <strong>Note:</strong> This project showcases modern full-stack development with React and Node.js, demonstrating AI integration capabilities and comprehensive workout management features.
                  </p>
                </div>
              </div>
              <div className="shawn-project-card reveal reveal-up">
                <div className="shawn-video-col">
                  <div className="shawn-video-container">
                    <iframe 
                      src="https://www.youtube.com/embed/GHky6jQOvuU?si=Ac4HxRMtCtfR8UQV" 
                      title="E-commerce App Demo" 
                      frameBorder="0" 
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" 
                      referrerPolicy="strict-origin-when-cross-origin" 
                      allowFullScreen>
                    </iframe>
                  </div>
                </div>
                <div className="shawn-info-col shawn-project-info">
                  <div className="shawn-project-timestamp">Created: <time dateTime="2025-06-17">June 17, 2025</time></div>
                  <h3>Modern E-commerce Platform 2.0</h3>
                  <p>
                    A cutting-edge e-commerce solution built with modern JavaScript frameworks and cloud technologies. This project showcases advanced frontend development with React, robust backend APIs with Node.js/Express, and scalable database architecture with PostgreSQL.
                  </p>
                  <h4>Key Features:</h4>
                  <ul>
                    <li>Modern React frontend with responsive design</li>
                    <li>RESTful API with Express.js backend</li>
                    <li>PostgreSQL database with advanced queries</li>
                    <li>Real-time inventory management</li>
                    <li>Secure payment gateway integration</li>
                    <li>Advanced admin dashboard with analytics</li>
                  </ul>
                  <h4>Cost:</h4>
                  <p>Time: 300 hours</p>
                  <p>A whole lot of Braincells</p>
                  <h4>Used Languages:</h4>
                  <div className="tech-stack">
                    <img src="/html.svg" alt="HTML" title="HTML5" data-tooltip="HTML5 - Semantic markup, modern web standards, accessibility" />
                    <img src="/css.svg" alt="CSS" title="CSS3" data-tooltip="CSS3 - Flexbox, Grid, animations, responsive design" />
                    <img src="/js.svg" alt="JavaScript" title="JavaScript" data-tooltip="JavaScript - ES6+, React hooks, modern syntax" />
                    <img src="/react.svg" alt="React" title="React" data-tooltip="React - Component architecture, state management, admin dashboard" />
                    <img src="/nodejs.svg" alt="Node.js" title="Node.js" data-tooltip="Node.js - Express.js backend, RESTful APIs, authentication" />
                    <img src="/sql.svg" alt="PostgreSQL" title="PostgreSQL" data-tooltip="PostgreSQL - Advanced queries, inventory management, analytics" />
                    <img src="/axios.svg" alt="Axios" title="Axios" data-tooltip="Axios - HTTP client, API integration, error handling" />
                  </div>
                </div>
              </div>
              <div className="shawn-project-card shawn-project-card--placeholder reveal reveal-fade">
                <div className="more-to-come">
                  <span className="more-to-come-mark" aria-hidden="true" />
                  <h3>More to come</h3>
                  <p>
                    I&rsquo;m always building something. New work shows up here as it
                    ships.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Resume Section */}
        <section className="section section--alt resume-section">
          <div className="container">
            <div className="section-head">
              <p className="eyebrow">Credentials</p>
              <h2 className="section-title reveal reveal-up">Resume.</h2>
              <p className="section-lede reveal reveal-up" style={{ '--reveal-delay': '0.08s' }}>
                Professional experience, skills, and achievements — read it here or take
                a copy.
              </p>
            </div>

            <div className="pdf-container reveal reveal-scale">
              <div className="pdf-header">
                <h3>Interactive resume</h3>
                <button
                  className="btn btn--ghost btn--sm"
                  onClick={() =>
                    handleDownload('/RESUME PROJECT - SHAWN DE PEDRO.pdf', 'ShawnResume')
                  }
                >
                  <svg className="download-icon" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                    <path
                      fillRule="evenodd"
                      d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z"
                      clipRule="evenodd"
                    />
                  </svg>
                  Download PDF
                </button>
              </div>
              <iframe
                src="/RESUME PROJECT - SHAWN DE PEDRO.pdf#toolbar=0&navpanes=0&scrollbar=0&view=FitH"
                className="pdf-viewer"
                title="Shawn's Resume"
              />
            </div>

            {/* Page images. Mobile browsers routinely refuse to render a PDF in
                an iframe, so these are the reliable way to actually read it. */}
            <div className="resume-pages" data-stagger="0.08">
              {[
                { src: '/ShawnResume1.png', label: 'Page 1' },
                { src: '/ShawnResume2.png', label: 'Page 2' },
              ].map((page) => (
                <figure className="resume-page reveal reveal-up" key={page.src}>
                  <img src={page.src} alt={`Resume ${page.label}`} loading="lazy" />
                  <figcaption>{page.label}</figcaption>
                </figure>
              ))}
            </div>
          </div>
        </section>

        {/* Contact Section */}
        {/* <section className="contact-section">
          <div className="container">
            <h2 className="section-title">Contact Information</h2>
            <p className="skills-subtitle">Feel free to reach out for collaborations, questions, or just to say hello!</p>
            
            <div className="contact-grid">
              <div className="contact-card">
                <h3>Email</h3>
                <a href="mailto:shawnysmedia@gmail.com" className="contact-link">
                  <img src="/mail.svg" alt="Email" />
                  <span>shawnysmedia@gmail.com</span>
                </a>
                <p>For business inquiries and project discussions</p>
              </div>

              <div className="contact-card">
                <h3>YouTube</h3>
                <a href="https://www.youtube.com/@Shawny-Media" target="_blank" rel="noopener noreferrer" className="contact-link">
                  <img src="/yt.svg" alt="YouTube" />
                  <span>@Shawny-Media</span>
                </a>
                <p>Check out my latest tutorials and project demos</p>
              </div>

              <div className="contact-card">
                <h3>Let's Connect</h3>
                <p>I'm always interested in hearing about new opportunities and exciting projects. Whether you have a question about my work, want to collaborate, or just want to chat about technology, feel free to reach out!</p>
              </div>
            </div>

            <div className="contact-info">
              <h3>What I Can Help With:</h3>
              <ul>
                <li>Web application development</li>
                <li>Frontend and backend solutions</li>
                <li>Database design and optimization</li>
                <li>Minecraft plugin development</li>
                <li>UI/UX improvements</li>
                <li>Technical consulting</li>
              </ul>
            </div>
          </div>
        </section> */}
      </main>
    </>
  );
};

export default Shawn; 